export const forte2Payment = {
  namespaced: true,
  state: () => ({
    // totals/fees object the parent builds (Phase 2 dialog consumes this)
    forte2PaymentData: null,
    forte2PaymentType: 'CREDIT',
    // flags driving the verify -> process flow
    isProcessing: false,
    isVerifying: false,
    isVerified: false,
    // results surfaced from createForte2Token's verify step
    verifiedToken: null,
    verifiedCardCategory: null,
    // eCheck verify-result state (mirrors the card verify fields above; dialog
    // form fields live dialog-local, only the verify RESULT lands in the store)
    isEcheckVerifying: false,
    isEcheckVerified: false,
    verifiedEcheckToken: null,
    echeckBankName: null, // from data.echeck.issuer_bank
    echeckPreauthResult: null, // from data.response.preauth_result
    // dialog visibility (parent opens, dialog closes) — mirrors payrixPayment
    isDialogOpen: false,
    // post-success persistence (mirrors payrixPayment) — drives the taxccpayments write
    paymentSuccessFlag: false,
    activeItemIndex: null,
    // captured success result the forte2PosReqMapping builder consumes
    transactionId: null,
    authorizationAmount: null,
    referenceId: null,
    cardholderName: null,
    billingFirstName: null,
    billingLastName: null,
    email: null,
    phone: null,
    principalAmount: null,
    processingFee: null,
    cardType: null,
    // One captured result per successful charge, so a payment split across multiple cards/
    // eChecks creates one taxccpayments record per charge. The singleton result fields above
    // only ever hold the LAST charge; this list keeps them all. Deduped by transactionId;
    // cleared after a fully-successful save and when a fresh payment begins.
    capturedForte2Payments: [],
  }),
  mutations: {
    setForte2PaymentData(state, payload) {
      state.forte2PaymentData = payload;
    },
    setForte2PaymentType(state, paymentType) {
      state.forte2PaymentType = paymentType;
    },
    setIsProcessing(state, flag) {
      state.isProcessing = flag;
    },
    setIsVerifying(state, flag) {
      state.isVerifying = flag;
    },
    setIsVerified(state, flag) {
      state.isVerified = flag;
    },
    setVerifiedToken(state, token) {
      state.verifiedToken = token;
    },
    setVerifiedCardCategory(state, cardCategory) {
      state.verifiedCardCategory = cardCategory;
    },
    // eCheck verify-result mutations (mirror the card verify setters above)
    setIsEcheckVerifying(state, flag) {
      state.isEcheckVerifying = flag;
    },
    setIsEcheckVerified(state, flag) {
      state.isEcheckVerified = flag;
    },
    setVerifiedEcheckToken(state, token) {
      state.verifiedEcheckToken = token;
    },
    setEcheckBankName(state, bankName) {
      state.echeckBankName = bankName;
    },
    setEcheckPreauthResult(state, preauthResult) {
      state.echeckPreauthResult = preauthResult;
    },
    setDialogOpen(state, flag) {
      state.isDialogOpen = flag;
    },
    // post-success persistence mutations (mirror payrixPayment)
    setForte2PaymentSuccessFlag(state, { flag, transactionId }) {
      state.paymentSuccessFlag = flag;
      state.transactionId = transactionId;
      // On a successful charge, snapshot THIS card/eCheck's result before the next charge
      // overwrites the singleton fields, so each charge can get its own taxccpayments record.
      // Deduped by transactionId. forte2PaymentType + principalAmount let the save-time mapper
      // match each snapshot back to its own paymentDetails row.
      if (flag && transactionId) {
        const alreadyCaptured = state.capturedForte2Payments
          .some((existing) => existing.transactionId === transactionId);
        if (!alreadyCaptured) {
          state.capturedForte2Payments = [...state.capturedForte2Payments, {
            transactionId,
            forte2PaymentType: state.forte2PaymentType,
            principalAmount: state.principalAmount,
            processingFee: state.processingFee,
            cardType: state.cardType,
            cardholderName: state.cardholderName,
            email: state.email,
            phone: state.phone,
          }];
        }
      }
    },
    setForte2ActiveItemIndex(state, idx) {
      state.activeItemIndex = idx;
    },
    setForte2PaymentResult(state, payload) {
      state.authorizationAmount = payload.authorizationAmount;
      state.referenceId = payload.referenceId;
      state.cardholderName = payload.cardholderName;
      state.billingFirstName = payload.billingFirstName;
      state.billingLastName = payload.billingLastName;
      state.email = payload.email;
      state.phone = payload.phone;
      state.principalAmount = payload.principalAmount;
      state.processingFee = payload.processingFee;
      state.cardType = payload.cardType || null;
    },
    clearCapturedForte2Payments(state) {
      state.capturedForte2Payments = [];
    },
    // drop only the verify result, keep forte2PaymentData (for the Cancel action)
    clearVerification(state) {
      state.isVerified = false;
      state.isVerifying = false;
      state.verifiedToken = null;
      state.verifiedCardCategory = null;
      state.isEcheckVerified = false;
      state.isEcheckVerifying = false;
      state.verifiedEcheckToken = null;
      state.echeckBankName = null;
      state.echeckPreauthResult = null;
    },
    // Reset ONLY the dialog-input/verify/visibility state. Mirrors Payrix's handleClose,
    // which never touches the post-success persistence fields. The success flag, active row
    // index, and captured result MUST survive dialog close so the (manual or auto) save can
    // still write the taxccpayments record — clearing them here was the early-reset bug.
    resetForte2DialogState(state) {
      state.forte2PaymentData = null;
      // forte2PaymentType is deliberately NOT reset here. forte2PosReqMapping reads it LIVE at
      // save time to route specialPaymentType (ECHECK_FORTE2 vs the card-brand mapper), and a save
      // can legitimately run after the dialog closes — resetting it to 'CREDIT' here made a closed
      // eCheck persist as OTHER_FORTE2. It belongs with the other post-success survivors below and
      // is cleared by resetForte2Payment. The launcher sets the tender on every open, so it cannot
      // go stale across rows.
      state.isProcessing = false;
      state.isVerifying = false;
      state.isVerified = false;
      state.verifiedToken = null;
      state.verifiedCardCategory = null;
      state.isEcheckVerifying = false;
      state.isEcheckVerified = false;
      state.verifiedEcheckToken = null;
      state.echeckBankName = null;
      state.echeckPreauthResult = null;
      state.isDialogOpen = false;
    },
    resetForte2Payment(state) {
      state.forte2PaymentData = null;
      state.forte2PaymentType = 'CREDIT';
      state.isProcessing = false;
      state.isVerifying = false;
      state.isVerified = false;
      state.verifiedToken = null;
      state.verifiedCardCategory = null;
      state.isEcheckVerifying = false;
      state.isEcheckVerified = false;
      state.verifiedEcheckToken = null;
      state.echeckBankName = null;
      state.echeckPreauthResult = null;
      state.isDialogOpen = false;
      // post-success persistence
      state.paymentSuccessFlag = false;
      state.activeItemIndex = null;
      state.transactionId = null;
      state.authorizationAmount = null;
      state.referenceId = null;
      state.cardholderName = null;
      state.billingFirstName = null;
      state.billingLastName = null;
      state.email = null;
      state.phone = null;
      state.principalAmount = null;
      state.processingFee = null;
      state.cardType = null;
    },
  },
  actions: {
    setForte2PaymentData({ commit }, data) {
      commit('setForte2PaymentData', data);
    },
    setForte2PaymentType({ commit }, paymentType) {
      commit('setForte2PaymentType', paymentType);
    },
    setIsProcessing({ commit }, flag) {
      commit('setIsProcessing', flag);
    },
    setIsVerifying({ commit }, flag) {
      commit('setIsVerifying', flag);
    },
    setIsEcheckVerifying({ commit }, flag) {
      commit('setIsEcheckVerifying', flag);
    },
    // record a successful tokenize/verify result
    setVerified({ commit }, { token, cardCategory }) {
      commit('setVerifiedToken', token);
      commit('setVerifiedCardCategory', cardCategory);
      commit('setIsVerified', true);
    },
    // record a successful eCheck verify result (mirrors setVerified)
    setEcheckVerified({ commit }, { token, bankName, preauthResult }) {
      commit('setVerifiedEcheckToken', token);
      commit('setEcheckBankName', bankName);
      commit('setEcheckPreauthResult', preauthResult);
      commit('setIsEcheckVerified', true);
    },
    toggleDialog({ commit }, flag) {
      commit('setDialogOpen', flag);
    },
    // post-success persistence actions (dispatch-style, mirror payrixPayment)
    setForte2PaymentSuccessFlag({ commit }, payload) {
      commit('setForte2PaymentSuccessFlag', payload);
    },
    setForte2ActiveItemIndex({ commit }, idx) {
      commit('setForte2ActiveItemIndex', idx);
    },
    setForte2PaymentResult({ commit }, payload) {
      commit('setForte2PaymentResult', payload);
    },
    clearCapturedForte2Payments({ commit }) {
      commit('clearCapturedForte2Payments');
    },
    clearVerification({ commit }) {
      commit('clearVerification');
    },
    resetForte2DialogState({ commit }) {
      commit('resetForte2DialogState');
    },
    resetForte2Payment({ commit }) {
      commit('resetForte2Payment');
    },
  },
  getters: {
    getForte2PaymentData: (state) => state.forte2PaymentData,
    getForte2PaymentType: (state) => state.forte2PaymentType,
    isProcessing: (state) => state.isProcessing,
    isVerifying: (state) => state.isVerifying,
    isVerified: (state) => state.isVerified,
    getVerifiedToken: (state) => state.verifiedToken,
    getVerifiedCardCategory: (state) => state.verifiedCardCategory,
    isDialogOpen: (state) => state.isDialogOpen,
    paymentSuccessFlag: (state) => state.paymentSuccessFlag,
    activeItemIndex: (state) => state.activeItemIndex,
    forte2PaymentResult: (state) => ({
      transactionId: state.transactionId,
      authorizationAmount: state.authorizationAmount,
      referenceId: state.referenceId,
      cardholderName: state.cardholderName,
      billingFirstName: state.billingFirstName,
      billingLastName: state.billingLastName,
      email: state.email,
      phone: state.phone,
      principalAmount: state.principalAmount,
      processingFee: state.processingFee,
    }),
  },
};

export default forte2Payment;
